import { useContext, useMemo } from 'react';

import { getCanvasCompositePopupTargets } from 'components/Studio/compositeUtils';
import DashboardGadgetsContext from 'contexts/DashboardGadgetsContext';
import DashboardsContext from 'contexts/DashboardsContext';
import GoalsContext from 'contexts/GoalsContext';
import MetricListsContext from 'contexts/MetricListsContext';
import ReportsContext from 'contexts/ReportsContext';
import ScorecardsContext from 'contexts/ScorecardsContext';
import kpiTypeCheckers from 'hooks/kpiTypeCheckers';
import cardTypeCheckers from 'types/cardTypeCheckers';

type DashboardPopupReportUsage = {
  type: 'dashboard';
  dashboard: DashboardType;
};

type KpiListPopupReportUsage = {
  type: 'kpi-list';
  kpiList: Scorecards.Scorecard;
};

type GoalPopupReportUsage = {
  type: 'goal';
  goal: GeneralGoal;
};

export type PopupReportUsage =
  | DashboardPopupReportUsage
  | KpiListPopupReportUsage
  | GoalPopupReportUsage;

export const isDashboardPopupReportUsage = (
  usage: PopupReportUsage,
): usage is DashboardPopupReportUsage => usage.type === 'dashboard';

export const isKpiListPopupReportUsage = (
  usage: PopupReportUsage,
): usage is KpiListPopupReportUsage => usage.type === 'kpi-list';

export const isGoalPopupReportUsage = (
  usage: PopupReportUsage,
): usage is GoalPopupReportUsage => usage.type === 'goal';

// Where each report is opened as a popup: { [reportId]: usage }. Dashboard
// gadgets only count once loaded (they're loaded on demand), so callers
// request all dashboard gadgets first.
export const buildPopupReportUsage = ({
  dashboards,
  dashboardGadgets,
  metricLists,
  scorecards,
  goals,
  onlyFromPublishedSources,
}: {
  dashboards: DashboardType[];
  dashboardGadgets: DashboardGadget[];
  metricLists: MetricListGadgetType[];
  scorecards: Scorecards.Scorecard[];
  goals: GeneralGoal[];
  onlyFromPublishedSources: boolean;
}): { [reportId: string]: PopupReportUsage[] } => {
  const popupIds: { [reportId: string]: PopupReportUsage[] } = {};
  const addUsage = ({
    reportId,
    newUsage,
  }: {
    reportId: string;
    newUsage: PopupReportUsage;
  }) => {
    if (popupIds[reportId]) {
      popupIds[reportId].push(newUsage);
    } else {
      popupIds[reportId] = [newUsage];
    }
  };
  const isIncluded = (source: { isV3Published: boolean }) =>
    !onlyFromPublishedSources || source.isV3Published;

  // The first dashboard holding each gadget and metric list
  const gadgetDashboards = new Map<string, DashboardType>();
  const metricListDashboards = new Map<string, DashboardType>();
  dashboards.forEach((dashboard) => {
    dashboard.canvas.cards.forEach((card) => {
      if (
        cardTypeCheckers.isDashboardGadget(card) &&
        !gadgetDashboards.has(card.content.dashboardGadgetId)
      ) {
        gadgetDashboards.set(card.content.dashboardGadgetId, dashboard);
      }
      if (
        cardTypeCheckers.isMetricList(card) &&
        !metricListDashboards.has(card.content.metricListId)
      ) {
        metricListDashboards.set(card.content.metricListId, dashboard);
      }
    });
  });

  metricLists.forEach((metricList) => {
    const dashboard = metricListDashboards.get(metricList.id);
    if (!dashboard || !isIncluded(dashboard)) {
      return;
    }

    metricList.list.forEach((listItem) => {
      if (listItem.reportDrillDownId) {
        addUsage({
          reportId: listItem.reportDrillDownId,
          newUsage: { type: 'dashboard', dashboard },
        });
      }
    });
  });

  dashboardGadgets.forEach((gadget) => {
    const dashboard = gadgetDashboards.get(gadget.id);
    if (gadget.reportDrillDownId && dashboard && isIncluded(dashboard)) {
      addUsage({
        reportId: gadget.reportDrillDownId,
        newUsage: { type: 'dashboard', dashboard },
      });
    }
  });

  // Composite chart elements carry their popup themselves (no gadget doc)
  dashboards.filter(isIncluded).forEach((dashboard) => {
    getCanvasCompositePopupTargets(dashboard.canvas.cards).reportIds.forEach(
      (reportId) => {
        addUsage({ reportId, newUsage: { type: 'dashboard', dashboard } });
      },
    );
  });

  scorecards.filter(isIncluded).forEach((scorecard) => {
    scorecard.kpis.filter(kpiTypeCheckers.isKpiRow).forEach((kpi) => {
      if (kpi.reportDrillDownId) {
        addUsage({
          reportId: kpi.reportDrillDownId,
          newUsage: { type: 'kpi-list', kpiList: scorecard },
        });
      }
    });
  });

  goals.filter(isIncluded).forEach((goal) => {
    goal.kpis.forEach((kpi) => {
      if (kpi.reportDrillDownId) {
        addUsage({
          reportId: kpi.reportDrillDownId,
          newUsage: { type: 'goal', goal },
        });
      }
    });
  });

  return popupIds;
};

const usePopupReports = ({
  onlyFromPublishedSources,
}: {
  onlyFromPublishedSources: boolean;
}) => {
  const { allReports } = useContext(ReportsContext);
  const { allGadgets } = useContext(DashboardGadgetsContext);
  const { metricLists } = useContext(MetricListsContext);
  const { allDashboards } = useContext(DashboardsContext);
  const { scorecards } = useContext(ScorecardsContext);
  const { goals } = useContext(GoalsContext);

  const popupReportIds = useMemo(
    () =>
      buildPopupReportUsage({
        dashboards: allDashboards,
        dashboardGadgets: allGadgets,
        metricLists,
        scorecards,
        goals,
        onlyFromPublishedSources,
      }),
    [
      allDashboards,
      allGadgets,
      goals,
      metricLists,
      onlyFromPublishedSources,
      scorecards,
    ],
  );

  const popupReports = useMemo((): {
    report: ReportType;
    usage: PopupReportUsage[];
  }[] => {
    const items: { report: ReportType; usage: PopupReportUsage[] }[] = [];
    Object.entries(popupReportIds).forEach(([reportId, usage]) => {
      const report = allReports.find((r) => r.id === reportId);
      if (report) {
        items.push({ report, usage });
      }
    });

    return items;
  }, [allReports, popupReportIds]);

  return popupReports;
};

export default usePopupReports;
